import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState, createContext, useContext } from "react";

type EditLang = "en" | "fi";
const EditLangContext = createContext<EditLang>("en");
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

import {
  getAdminStatus, listAllEntries, claimFirstAdmin,
  upsertEntry, deleteEntry, bulkUpsertEntries, uploadSiteImage,
  listAdmins, createAdmin, removeAdmin,
} from "@/lib/admin.functions";
import { getAnalytics } from "@/lib/analytics.functions";
import {
  listApplications, updateApplicationStatus, deleteApplication,
  type Application,
} from "@/lib/applications.functions";
import { exportDatabase, importDatabase } from "@/lib/migration.functions";
import { DEFAULT_CONTENT } from "@/content/defaults";


export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({ meta: [{ title: "Content Admin — Tacoya" }, { name: "robots", content: "noindex" }] }),
  component: AdminPage,
});

type DBEntry = {
  id: string; page: string; section: string; item_key: string;
  sort_order: number; is_published: boolean;
  data: Record<string, unknown>; image_url: string | null;
};

const isTField = (v: unknown): v is { en: string; fi: string } =>
  !!v && typeof v === "object" && "en" in (v as object) && "fi" in (v as object);

function AdminPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const statusFn = useServerFn(getAdminStatus);
  const listFn = useServerFn(listAllEntries);
  const claimFn = useServerFn(claimFirstAdmin);
  const bulkFn = useServerFn(bulkUpsertEntries);

  const status = useQuery({ queryKey: ["admin-status"], queryFn: () => statusFn() });
  const isAdmin = status.data?.isAdmin ?? false;
  const isSuperAdmin = status.data?.isSuperAdmin ?? false;
  const [view, setView] = useState<View>("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useState<EditLang>("en");

  const entries = useQuery({
    queryKey: ["admin-entries"],
    queryFn: () => listFn() as Promise<DBEntry[]>,
    enabled: isAdmin,
  });

  const claim = useMutation({
    mutationFn: () => claimFn(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-status"] }); },
  });

  const importDefaults = useMutation({
    mutationFn: () => bulkFn({
      data: {
        entries: DEFAULT_CONTENT.map((e) => ({
          page: e.page, section: e.section, item_key: e.key,
          sort_order: e.order, is_published: true,
          data: e.data as Record<string, unknown>, image_url: e.image ?? null,
        })),
      },
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-entries"] });
      qc.invalidateQueries({ queryKey: ["site-content"] });
    },
  });

  async function signOut() {
    await qc.cancelQueries();
    qc.clear();
    const { supabase } = await import("@/integrations/supabase/client");
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  if (status.isLoading) {
    return <Centered>Loading…</Centered>;
  }

  if (!isAdmin) {
    const noAdminYet = (status.data?.adminCount ?? 0) === 0;
    return (
      <Centered>
        <div className="max-w-md text-center">
          <h1 className="font-display text-4xl text-cream uppercase">Admin Access</h1>
          {noAdminYet ? (
            <>
              <p className="mt-3 text-cream/65">No admin exists yet. Claim admin access for this account to manage all site content.</p>
              <button
                onClick={() => claim.mutate()} disabled={claim.isPending}
                className="mt-6 rounded-full bg-yellow text-charcoal px-6 py-3 text-sm uppercase tracking-widest font-bold hover:bg-cream disabled:opacity-60"
              >
                {claim.isPending ? "Claiming…" : "Make me admin"}
              </button>
              {claim.error && <p className="mt-3 text-sm text-chili">{(claim.error as Error).message}</p>}
            </>
          ) : (
            <p className="mt-3 text-cream/65">This account doesn't have admin access. Ask an existing admin to grant it.</p>
          )}
          <button onClick={signOut} className="mt-6 block mx-auto text-xs text-cream/50 hover:text-yellow">Sign out</button>
        </div>
      </Centered>
    );
  }

  const list = entries.data ?? [];


  const pageNav: { id: string; label: string; icon: string }[] = [
    { id: "home", label: "Home", icon: "house" },
    { id: "menu", label: "Menu", icon: "grid" },
    { id: "locations", label: "Locations", icon: "pin" },
    { id: "franchise", label: "Franchise", icon: "briefcase" },
    { id: "shop", label: "Shop", icon: "grid" },

    { id: "header", label: "Header", icon: "layout" },
    { id: "footer", label: "Footer", icon: "layout" },
  ];
  const allNav = [
    { id: "dashboard", label: "Dashboard", icon: "speedometer2" },
    ...pageNav,
    { id: "applications", label: "Applications", icon: "inbox" },
    ...(isSuperAdmin ? [{ id: "admins", label: "Admins", icon: "people" }] : []),
    { id: "migration", label: "Backup", icon: "database" },
  ];
  const currentLabel = allNav.find((n) => n.id === view)?.label ?? "Dashboard";
  const isPageView = pageNav.some((n) => n.id === view);

  const navButton = (n: { id: string; label: string; icon: string }) => (
    <button
      key={n.id}
      onClick={() => setView(n.id)}
      className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
        view === n.id ? "bg-yellow text-charcoal font-semibold" : "text-cream/70 hover:bg-cream/10"
      }`}
    >
      <NavIcon name={n.icon} />
      {n.label}
    </button>
  );

  return (
    <EditLangContext.Provider value={lang}>
    <div className="min-h-dvh bg-charcoal text-cream flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-cream/10 bg-card/60">
        <div className="px-5 py-5 border-b border-cream/10">
          <span className="font-display text-2xl text-yellow uppercase tracking-wide">Tacoya</span>
          <p className="text-[10px] text-cream/45 uppercase tracking-widest">Admin Console</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navButton({ id: "dashboard", label: "Dashboard", icon: "speedometer2" })}
          <p className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-widest text-cream/35">Pages</p>
          {pageNav.map(navButton)}
          <p className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-widest text-cream/35">Leads</p>
          {navButton({ id: "applications", label: "Applications", icon: "inbox" })}
          {isSuperAdmin && (
            <>
              <p className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-widest text-cream/35">Access</p>
              {navButton({ id: "admins", label: "Admins", icon: "people" })}
            </>
          )}
          <p className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-widest text-cream/35">System</p>
          {navButton({ id: "migration", label: "Backup & Migrate", icon: "database" })}
        </nav>
        <div className="p-3 border-t border-cream/10">
          <button onClick={signOut}
            className="w-full flex items-center gap-3 rounded-xl border border-chili/40 bg-chili/15 px-3 py-3 text-sm font-semibold text-chili transition-colors hover:bg-chili hover:text-cream">
            <span className="grid size-8 place-items-center rounded-lg bg-chili/20"><NavIcon name="power" /></span>
            Sign out
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="border-b border-cream/10 bg-charcoal/95 px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="font-display text-lg sm:text-xl text-cream uppercase tracking-wide truncate">{currentLabel}</h1>
              <p className="text-[11px] text-cream/45 truncate">Tacoya content management · EN + FI</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {/* Language switch */}
              <div className="flex rounded-full border border-cream/15 bg-card p-0.5 text-[11px] font-bold uppercase tracking-widest">
                {(["en", "fi"] as const).map((l) => (
                  <button key={l} onClick={() => setLang(l)}
                    className={`rounded-full px-3 py-1.5 transition-colors ${lang === l ? "bg-yellow text-charcoal" : "text-cream/60 hover:text-cream"}`}
                  >{l}</button>
                ))}
              </div>
              {list.length === 0 && (
                <button
                  onClick={() => importDefaults.mutate()} disabled={importDefaults.isPending}
                  className="rounded-full bg-yellow text-charcoal px-3 sm:px-4 py-2 text-[11px] sm:text-xs uppercase tracking-widest font-bold hover:bg-cream disabled:opacity-60"
                >{importDefaults.isPending ? "Importing…" : "Import"}</button>
              )}
            {/* Mobile nav access menu */}
            <button
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Navigation menu"
              className="md:hidden shrink-0 grid size-10 place-items-center rounded-xl border border-cream/15 bg-card text-cream"
            >
              <NavIcon name={menuOpen ? "x" : "list"} />
            </button>
            </div>
          </div>
        </header>

        {/* Mobile nav dropdown */}
        {menuOpen && (
          <div className="md:hidden border-b border-cream/10 bg-charcoal/98">
            <nav className="p-3 space-y-1">
              {allNav.map((n) => (
                <button
                  key={n.id}
                  onClick={() => { setView(n.id); setMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                    view === n.id ? "bg-yellow text-charcoal font-semibold" : "text-cream/70 hover:bg-cream/10"
                  }`}
                >
                  <NavIcon name={n.icon} /> {n.label}
                </button>
              ))}
              <button onClick={signOut}
                className="mt-2 w-full flex items-center gap-3 rounded-xl border border-chili/40 bg-chili/15 px-3 py-3 text-sm font-semibold text-chili transition-colors hover:bg-chili hover:text-cream">
                <span className="grid size-8 place-items-center rounded-lg bg-chili/20"><NavIcon name="power" /></span>
                Sign out
              </button>
            </nav>
          </div>
        )}



        <main className="flex-1 p-4 sm:p-6 pb-24 md:pb-6">
          {view === "dashboard" && (
            <Dashboard
              isAdmin={isAdmin}
              contentCount={list.length}
              pageCounts={list.reduce<Record<string, number>>((acc, e) => { acc[e.page] = (acc[e.page] ?? 0) + 1; return acc; }, {})}
              onOpenPage={setView}
            />
          )}


          {isPageView && (
            <div>
              {entries.isLoading && <p className="text-cream/60">Loading content…</p>}
              {!entries.isLoading && list.filter((e) => e.page === view).length === 0 && (
                <div className="rounded-2xl border border-cream/10 bg-card p-8 text-center">
                  <p className="text-cream/70">No content for this page yet.</p>
                  <p className="mt-1 text-sm text-cream/50">Click “Import content” in the top bar to load every section from the live site, then edit freely.</p>
                </div>
              )}
              {list.filter((e) => e.page === view).length > 0 && (
                <ContentBoard entries={list.filter((e) => e.page === view)} />
              )}
            </div>
          )}

          {view === "applications" && <ApplicationsManager isAdmin={isAdmin} />}

          {view === "admins" && isSuperAdmin && <AdminManager />}

          {view === "migration" && <MigrationManager />}
        </main>
      </div>

      {/* App-style bottom navigation (mobile) */}
      <nav className="md:hidden fixed inset-x-0 bottom-0 z-30 border-t border-cream/10 bg-charcoal/95 backdrop-blur pb-[env(safe-area-inset-bottom)]">
        <div className="flex gap-1 overflow-x-auto px-2 py-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {allNav.map((n) => (
            <button key={n.id} onClick={() => setView(n.id)}
              className={`flex shrink-0 basis-[19%] min-w-[64px] flex-col items-center gap-1 rounded-xl px-1 py-1.5 transition-colors ${
                view === n.id ? "text-yellow" : "text-cream/55 hover:text-cream"
              }`}>
              <span className={`grid size-9 place-items-center rounded-xl transition-colors ${view === n.id ? "bg-yellow/15" : ""}`}>
                <NavIcon name={n.icon} />
              </span>
              <span className="text-[10px] leading-none tracking-wide">{n.label}</span>
            </button>
          ))}
        </div>
      </nav>

    </div>
    </EditLangContext.Provider>
  );
}

type View = string;

function Dashboard({ isAdmin, contentCount, pageCounts, onOpenPage }: {
  isAdmin: boolean;
  contentCount: number;
  pageCounts: Record<string, number>;
  onOpenPage: (page: string) => void;
}) {
  const analyticsFn = useServerFn(getAnalytics);
  const a = useQuery({
    queryKey: ["analytics"],
    queryFn: () => analyticsFn(),
    enabled: isAdmin,
    refetchInterval: 60_000,
  });

  const stats = a.data;
  const deviceData = stats
    ? [
        { name: "Desktop", value: stats.device.desktop, color: "#f5b301" },
        { name: "Mobile", value: stats.device.mobile, color: "#e2483d" },
        { name: "Tablet", value: stats.device.tablet, color: "#7dd3a8" },
      ].filter((d) => d.value > 0)
    : [];
  const totalDevices = stats ? stats.device.desktop + stats.device.mobile + stats.device.tablet : 0;
  const mobilePct = totalDevices ? Math.round((stats!.device.mobile / totalDevices) * 100) : 0;
  const desktopPct = totalDevices ? Math.round((stats!.device.desktop / totalDevices) * 100) : 0;

  const fmtDay = (d: string) => d.slice(5);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total visits (30d)" value={stats?.totalViews ?? 0} icon="grid" accent="yellow" />
        <StatCard label="Unique visitors (30d)" value={stats?.uniqueVisitors ?? 0} icon="people" accent="green" />
        <StatCard label="Visitors today" value={stats?.visitorsToday ?? 0} icon="check" accent="cream" />
        <StatCard label="Views today" value={stats?.viewsToday ?? 0} icon="pencil" accent="chili" />
      </div>

      {a.isLoading && <p className="text-cream/60 text-sm">Loading analytics…</p>}
      {a.error && <p className="text-chili text-sm">{(a.error as Error).message}</p>}

      {stats && stats.totalViews === 0 && (
        <div className="rounded-2xl border border-cream/10 bg-card p-8 text-center">
          <p className="text-cream/70">No visits recorded yet.</p>
          <p className="mt-1 text-sm text-cream/50">Visitor data appears here automatically as people browse the public site (real IP-based counts).</p>
        </div>
      )}

      {stats && stats.totalViews > 0 && (
        <>
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Traffic trend */}
            <div className="lg:col-span-2 rounded-2xl border border-cream/10 bg-card p-5">
              <h2 className="font-display text-base text-cream uppercase tracking-wide mb-4">Visitors & views · last 14 days</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.days}>
                    <defs>
                      <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f5b301" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#f5b301" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gVis" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7dd3a8" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#7dd3a8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(245,237,220,0.08)" />
                    <XAxis dataKey="date" tickFormatter={fmtDay} stroke="rgba(245,237,220,0.5)" fontSize={11} />
                    <YAxis stroke="rgba(245,237,220,0.5)" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#1a1410", border: "1px solid rgba(245,237,220,0.15)", borderRadius: 12, color: "#f5edDC" }} />
                    <Area type="monotone" dataKey="views" name="Views" stroke="#f5b301" fill="url(#gViews)" strokeWidth={2} />
                    <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#7dd3a8" fill="url(#gVis)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Device split */}
            <div className="rounded-2xl border border-cream/10 bg-card p-5">
              <h2 className="font-display text-base text-cream uppercase tracking-wide mb-4">Desktop vs Mobile</h2>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={deviceData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={3}>
                      {deviceData.map((d) => <Cell key={d.name} fill={d.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#1a1410", border: "1px solid rgba(245,237,220,0.15)", borderRadius: 12, color: "#f5edDC" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 space-y-1.5 text-sm">
                <div className="flex items-center justify-between"><span className="text-cream/70">🖥 Desktop</span><span className="text-cream">{stats.device.desktop} · {desktopPct}%</span></div>
                <div className="flex items-center justify-between"><span className="text-cream/70">📱 Mobile</span><span className="text-cream">{stats.device.mobile} · {mobilePct}%</span></div>
                <div className="flex items-center justify-between"><span className="text-cream/70">📲 Tablet</span><span className="text-cream">{stats.device.tablet}</span></div>
              </div>
            </div>
          </div>

          {/* Top pages */}
          <div className="rounded-2xl border border-cream/10 bg-card p-5">
            <h2 className="font-display text-base text-cream uppercase tracking-wide mb-4">Most visited pages</h2>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.topPages} layout="vertical" margin={{ left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(245,237,220,0.08)" horizontal={false} />
                  <XAxis type="number" stroke="rgba(245,237,220,0.5)" fontSize={11} allowDecimals={false} />
                  <YAxis type="category" dataKey="path" stroke="rgba(245,237,220,0.5)" fontSize={11} width={90} />
                  <Tooltip contentStyle={{ background: "#1a1410", border: "1px solid rgba(245,237,220,0.15)", borderRadius: 12, color: "#f5edDC" }} cursor={{ fill: "rgba(245,237,220,0.05)" }} />
                  <Bar dataKey="count" name="Visits" fill="#f5b301" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* Content overview */}
      <div className="rounded-2xl border border-cream/10 bg-card p-5 sm:p-6">
        <h2 className="font-display text-base text-cream uppercase tracking-wide mb-4">Content · {contentCount} items</h2>
        {contentCount === 0 ? (
          <p className="text-sm text-cream/60">No content yet. Use “Import content” to load every section from the live site.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(pageCounts).map(([page, n]) => (
              <button key={page} onClick={() => onOpenPage(page)}
                className="flex items-center justify-between rounded-xl border border-cream/10 bg-charcoal/40 px-4 py-3 text-left hover:border-yellow transition-colors">
                <span className="text-sm text-cream">{PAGE_LABELS[page] ?? page}</span>
                <span className="rounded-full bg-cream/10 px-2.5 py-0.5 text-xs text-cream/70">{n}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}




function StatCard({ label, value, icon, accent }: { label: string; value: number; icon: string; accent: string }) {
  const accentMap: Record<string, string> = {
    yellow: "text-yellow", green: "text-emerald-400", cream: "text-cream", chili: "text-chili",
  };
  return (
    <div className="rounded-2xl border border-cream/10 bg-card p-5 flex items-center justify-between">
      <div>
        <div className="text-3xl font-display text-cream">{value}</div>
        <div className="mt-1 text-[11px] uppercase tracking-widest text-cream/50">{label}</div>
      </div>
      <div className={`grid size-11 place-items-center rounded-xl bg-charcoal/50 ${accentMap[accent] ?? "text-cream"}`}>
        <NavIcon name={icon} />
      </div>
    </div>
  );
}

const STATUS_META: Record<string, { label: string; cls: string }> = {
  new: { label: "New", cls: "border-yellow/40 bg-yellow/15 text-yellow" },
  reviewing: { label: "Reviewing", cls: "border-cream/30 bg-cream/10 text-cream" },
  approved: { label: "Approved", cls: "border-emerald-400/40 bg-emerald-400/15 text-emerald-400" },
  rejected: { label: "Rejected", cls: "border-chili/40 bg-chili/15 text-chili" },
};
const STATUS_OPTIONS = ["new", "reviewing", "approved", "rejected"] as const;

function ApplicationsManager({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const listFn = useServerFn(listApplications);
  const statusFn = useServerFn(updateApplicationStatus);
  const deleteFn = useServerFn(deleteApplication);

  const apps = useQuery({
    queryKey: ["applications"],
    queryFn: () => listFn() as Promise<Application[]>,
    enabled: isAdmin,
  });

  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: (typeof STATUS_OPTIONS)[number] }) =>
      statusFn({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });

  const list = apps.data ?? [];
  const [filter, setFilter] = useState<"all" | (typeof STATUS_OPTIONS)[number]>("all");
  const fmt = (d: string) =>
    new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

  const counts = {
    new: list.filter((a) => a.status === "new").length,
    reviewing: list.filter((a) => a.status === "reviewing").length,
    approved: list.filter((a) => a.status === "approved").length,
    rejected: list.filter((a) => a.status === "rejected").length,
  };
  const filtered = filter === "all" ? list : list.filter((a) => a.status === filter);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-cream">Franchise Applications</h2>
          <p className="mt-1 text-sm text-cream/55">
            {list.length} total {list.length === 1 ? "submission" : "submissions"} from the Franchise page
          </p>
        </div>
        <button
          onClick={() => apps.refetch()}
          className="flex items-center gap-2 rounded-full border border-cream/15 bg-card px-4 py-2 text-[11px] uppercase tracking-widest font-bold text-cream/70 hover:text-cream"
        >Refresh</button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="New" value={counts.new} icon="inbox" accent="yellow" />
        <StatCard label="Reviewing" value={counts.reviewing} icon="inbox" accent="cream" />
        <StatCard label="Approved" value={counts.approved} icon="inbox" accent="green" />
        <StatCard label="Rejected" value={counts.rejected} icon="inbox" accent="chili" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(["all", ...STATUS_OPTIONS] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full px-3.5 py-1.5 text-[11px] uppercase tracking-widest font-bold transition-colors ${
              filter === f
                ? "bg-yellow text-charcoal"
                : "border border-cream/15 text-cream/60 hover:text-cream"
            }`}
          >
            {f === "all" ? `All (${list.length})` : `${STATUS_META[f].label} (${counts[f]})`}
          </button>
        ))}
      </div>

      {apps.isLoading && <p className="text-cream/60 text-sm">Loading applications…</p>}
      {apps.error && <p className="text-chili text-sm">{(apps.error as Error).message}</p>}

      {!apps.isLoading && list.length === 0 && (
        <div className="rounded-2xl border border-cream/10 bg-card p-8 text-center">
          <p className="text-cream/70">No applications yet.</p>
          <p className="mt-1 text-sm text-cream/50">Submissions from the Franchise page appear here automatically.</p>
        </div>
      )}

      {!apps.isLoading && list.length > 0 && filtered.length === 0 && (
        <div className="rounded-2xl border border-cream/10 bg-card p-8 text-center">
          <p className="text-cream/70">No {filter} applications.</p>
        </div>
      )}

      <div className="grid gap-4">
        {filtered.map((app) => {
          const meta = STATUS_META[app.status] ?? STATUS_META.new;
          return (
            <article key={app.id} className="rounded-2xl border border-cream/10 bg-card overflow-hidden">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-cream/10 px-5 py-3.5">
                <div className="min-w-0">
                  <h3 className="font-display text-lg text-cream truncate">{app.name}</h3>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-cream/55">
                    <a href={`mailto:${app.email}`} className="hover:text-yellow">{app.email}</a>
                    <span>· {app.city}</span>
                    <span>· {fmt(app.created_at)}</span>
                  </div>
                </div>
                <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold ${meta.cls}`}>
                  {meta.label}
                </span>
              </div>
              <div className="p-5">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-cream/75">{app.message}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] uppercase tracking-widest text-cream/40">Status</span>
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      disabled={setStatus.isPending}
                      onClick={() => setStatus.mutate({ id: app.id, status: s })}
                      className={`rounded-full px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold transition-colors disabled:opacity-50 ${
                        app.status === s
                          ? "bg-yellow text-charcoal"
                          : "border border-cream/15 text-cream/60 hover:text-cream"
                      }`}
                    >{STATUS_META[s].label}</button>
                  ))}
                  <button
                    onClick={() => { if (confirm("Delete this application permanently?")) remove.mutate(app.id); }}
                    disabled={remove.isPending}
                    className="ml-auto flex items-center gap-1.5 rounded-full border border-chili/40 bg-chili/10 px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold text-chili hover:bg-chili hover:text-cream disabled:opacity-50"
                  >
                    <NavIcon name="trash" /> Delete
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function NavIcon({ name }: { name: string }) {
  const paths: Record<string, string> = {
    speedometer2: "M12 4a8 8 0 016.32 12.9l-1.6-1.2A6 6 0 1012 6V4zm0 8l4-3",
    grid: "M4 4h7v7H4V4zm9 0h7v7h-7V4zM4 13h7v7H4v-7zm9 0h7v7h-7v-7z",
    people: "M16 11a3 3 0 100-6 3 3 0 000 6zm-8 0a3 3 0 100-6 3 3 0 000 6zm0 2c-2.7 0-6 1.3-6 4v2h8m6-6c2.7 0 6 1.3 6 4v2h-8",
    "box-arrow": "M14 3h7v7m0-7L10 14M19 14v5a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h5",
    power: "M12 4v8m5-5a7 7 0 11-10 0",
    check: "M5 12l4 4L19 7",
    pencil: "M4 20h4L18 10l-4-4L4 16v4zM14 6l4 4",
    house: "M3 11l9-7 9 7M5 10v10h14V10",
    pin: "M12 21s7-6.3 7-11a7 7 0 10-14 0c0 4.7 7 11 7 11zm0-8.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z",
    briefcase: "M4 7h16v13H4V7zm5 0V5a2 2 0 012-2h2a2 2 0 012 2v2",
    layout: "M3 5h18v14H3V5zm0 5h18M9 10v9",
    list: "M4 6h16M4 12h16M4 18h16",
    inbox: "M4 13h4l2 3h4l2-3h4M4 13l2-7h12l2 7v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5z",
    trash: "M4 7h16M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2m-9 0v12a2 2 0 002 2h6a2 2 0 002-2V7",
    x: "M6 6l12 12M18 6L6 18",
    database: "M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3zM4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6",
    download: "M12 3v12m0 0l-4-4m4 4l4-4M4 21h16",
    upload: "M12 21V9m0 0l-4 4m4-4l4 4M4 3h16",
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="size-5">
      <path d={paths[name] ?? paths.grid} />
    </svg>
  );
}

function AdminManager() {
  const qc = useQueryClient();
  const listFn = useServerFn(listAdmins);
  const createFn = useServerFn(createAdmin);
  const removeFn = useServerFn(removeAdmin);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  type Admin = { user_id: string; email: string; created_at: string; is_self: boolean; is_super: boolean };
  const admins = useQuery({ queryKey: ["admins"], queryFn: () => listFn() as Promise<Admin[]> });

  const create = useMutation({
    mutationFn: () => createFn({ data: { email, password } }),
    onSuccess: () => {
      setEmail(""); setPassword("");
      qc.invalidateQueries({ queryKey: ["admins"] });
    },
  });

  const revoke = useMutation({
    mutationFn: (user_id: string) => removeFn({ data: { user_id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admins"] }),
  });

  return (
    <div className="space-y-6">
      {/* Add admin card */}
      <section className="rounded-2xl border border-cream/10 bg-card overflow-hidden">
        <div className="border-b border-cream/10 px-5 py-3.5">
          <h2 className="font-display text-base text-cream uppercase tracking-wide">Add new admin</h2>
        </div>
        <div className="p-5">
          <p className="text-sm text-cream/55 mb-4">New admins sign in with the email &amp; password you set here.</p>
          <form
            onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
            className="grid sm:grid-cols-[1fr_1fr_auto] gap-2"
          >
            <input
              type="email" required placeholder="new-admin@tacoya.fi" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-lg border border-cream/15 bg-charcoal/60 px-4 py-2.5 text-cream text-sm outline-none focus:border-yellow"
            />
            <input
              type="password" required minLength={6} placeholder="Temp password (min 6)" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-lg border border-cream/15 bg-charcoal/60 px-4 py-2.5 text-cream text-sm outline-none focus:border-yellow"
            />
            <button
              type="submit" disabled={create.isPending}
              className="rounded-lg bg-yellow text-charcoal px-5 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-cream disabled:opacity-60"
            >{create.isPending ? "Adding…" : "Add admin"}</button>
          </form>
          {create.error && <p className="mt-3 text-sm text-chili">{(create.error as Error).message}</p>}
        </div>
      </section>

      {/* Admins table card */}
      <section className="rounded-2xl border border-cream/10 bg-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-cream/10 px-5 py-3.5">
          <h2 className="font-display text-base text-cream uppercase tracking-wide">Admin accounts</h2>
          <span className="rounded-full bg-cream/10 px-2.5 py-0.5 text-xs text-cream/70">{admins.data?.length ?? 0}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cream/10 text-left text-[11px] uppercase tracking-widest text-cream/45">
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {admins.isLoading && (
                <tr><td colSpan={3} className="px-5 py-4 text-cream/60">Loading admins…</td></tr>
              )}
              {admins.data?.map((a) => (
                <tr key={a.user_id} className="border-b border-cream/5 last:border-0 hover:bg-cream/5">
                  <td className="px-5 py-3 text-cream">{a.email}</td>
                  <td className="px-5 py-3">
                    {a.is_super ? (
                      <span className="rounded-full bg-yellow/15 px-2.5 py-0.5 text-[11px] uppercase tracking-widest text-yellow">Super admin</span>
                    ) : (
                      <span className="rounded-full bg-emerald-400/15 px-2.5 py-0.5 text-[11px] uppercase tracking-widest text-emerald-400">Admin</span>
                    )}
                    {a.is_self && (
                      <span className="ml-2 rounded-full bg-cream/10 px-2.5 py-0.5 text-[11px] uppercase tracking-widest text-cream/70">You</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {a.is_self ? (
                      <span className="text-[11px] text-cream/40">Current account</span>
                    ) : a.is_super ? (
                      <span className="text-[11px] text-cream/40">Protected</span>
                    ) : (
                      <button
                        onClick={() => { if (confirm(`Remove admin access from ${a.email}?`)) revoke.mutate(a.user_id); }}
                        disabled={revoke.isPending}
                        className="rounded-lg border border-chili/40 px-3 py-1.5 text-xs text-chili hover:bg-chili/10 disabled:opacity-60"
                      >Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {revoke.error && <p className="px-5 py-3 text-sm text-chili">{(revoke.error as Error).message}</p>}
      </section>
    </div>
  );
}

const PAGE_LABELS: Record<string, string> = {
  header: "Header", footer: "Footer", home: "Home Page",
  menu: "Menu Page", locations: "Locations Page", franchise: "Franchise Page",
  shop: "Shop Page",
};


const SECTION_META: Record<string, { label: string; hint: string }> = {
  hero: { label: "Hero", hint: "Main banner at the top of the page" },
  marquee_build: { label: "Accent Marquee", hint: "Scrolling accent strip" },
  marquee: { label: "Marquee", hint: "Scrolling text banner" },
  born_streets: { label: "Born in the Streets", hint: "Brand story media block" },
  media: { label: "Media", hint: "Featured media / video block" },
  media_items: { label: "Media Items", hint: "Recognition & press logos" },
  press_carousel: { label: "Press Carousel", hint: "Rotating press quotes" },
  numbers: { label: "Numbers Heading", hint: "Stats section title" },
  numbers_items: { label: "Numbers / Stats", hint: "Individual stat tiles" },
  key_message: { label: "Key Message Heading", hint: "Why Tacoya title" },
  key_message_lines: { label: "Key Message Lines", hint: "Why Tacoya bullet lines" },
  best_sellers: { label: "Best Sellers Heading", hint: "Best sellers title" },
  best_sellers_items: { label: "Best Sellers", hint: "Featured menu items" },
  community: { label: "Community Heading", hint: "Community wall title" },
  community_tiles: { label: "Community Wall", hint: "Photo grid tiles" },
  edge_items: { label: "Edge Items", hint: "Edge / value props" },
  trajectory: { label: "Trajectory Heading", hint: "Timeline title" },
  trajectory_items: { label: "Trajectory / Timeline", hint: "Roadmap phases" },
  cta: { label: "Call To Action", hint: "Bottom CTA block" },
  products: { label: "Products", hint: "Shop drop products" },
  notify: { label: "Notify / Signup", hint: "Early access signup block" },
  birrieria: { label: "Birrieria", hint: "Birrieria highlight block & background image" },
  featured: { label: "Featured Drop", hint: "Featured product spotlight & image" },
  lookbook: { label: "Lookbook Heading", hint: "Lookbook gallery title" },
  lookbook_tiles: { label: "Lookbook Gallery", hint: "Lookbook photo tiles" },

  items: { label: "Items", hint: "List items for this page" },
  stats: { label: "Stats", hint: "Numbers / statistics" },
  pillars: { label: "Pillars", hint: "Key value pillars" },
  roadmap: { label: "Roadmap", hint: "Roadmap phases" },
  form: { label: "Form Labels", hint: "Contact / application form text" },
  nav: { label: "Navigation Links", hint: "Menu / nav links" },
  brand: { label: "Brand", hint: "Logo & brand name" },
  socials: { label: "Social Links", hint: "Social media URLs" },
  legal: { label: "Legal", hint: "Copyright & legal text" },
};

const sectionLabel = (s: string) => SECTION_META[s]?.label ?? s.replace(/_/g, " ");


function ContentBoard({ entries }: { entries: DBEntry[] }) {
  const sections = useMemo(() => {
    const bySection: Record<string, DBEntry[]> = {};
    for (const e of entries) {
      bySection[e.section] ??= [];
      bySection[e.section].push(e);
    }
    return bySection;
  }, [entries]);

  const sectionKeys = Object.keys(sections);

  const scrollTo = (section: string) => {
    document.getElementById(`section-${section}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="space-y-6">
      {sectionKeys.length > 1 && (
        <div className="-mx-1 flex flex-wrap gap-2 rounded-2xl border border-cream/10 bg-card p-3">
          {sectionKeys.map((section) => (
            <button
              key={section}
              type="button"
              onClick={() => scrollTo(section)}
              className="rounded-full border border-cream/15 bg-charcoal/40 px-3.5 py-1.5 text-xs font-medium tracking-wide text-cream/80 transition hover:border-yellow hover:text-cream"
            >
              {sectionLabel(section)}
              <span className="ml-1.5 text-cream/40">{sections[section].length}</span>
            </button>
          ))}
        </div>
      )}
      {Object.entries(sections).map(([section, items]) => (
        <section id={`section-${section}`} key={section} className="scroll-mt-32 rounded-2xl border border-cream/10 bg-card overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-cream/10 px-5 py-3.5">
            <div className="min-w-0">
              <h2 className="font-display text-base text-cream uppercase tracking-wide truncate">
                {sectionLabel(section)}
              </h2>
              {SECTION_META[section]?.hint && (
                <p className="text-[11px] text-cream/45 truncate">{SECTION_META[section].hint}</p>
              )}
            </div>
            <span className="shrink-0 rounded-full bg-cream/10 px-2.5 py-0.5 text-xs text-cream/70">{items.length}</span>
          </div>
          <div className="p-4 sm:p-5 space-y-4">
            {items.map((e) => <EntryEditor key={e.id} entry={e} />)}
          </div>
        </section>
      ))}
    </div>
  );
}



function EntryEditor({ entry }: { entry: DBEntry }) {
  const qc = useQueryClient();
  const saveFn = useServerFn(upsertEntry);
  const delFn = useServerFn(deleteEntry);
  const uploadFn = useServerFn(uploadSiteImage);

  const editLang = useContext(EditLangContext);
  const [data, setData] = useState<Record<string, unknown>>(entry.data ?? {});
  const [imageUrl, setImageUrl] = useState<string | null>(entry.image_url);
  const [published, setPublished] = useState(entry.is_published);
  const [order, setOrder] = useState(entry.sort_order);
  const [uploading, setUploading] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [open, setOpen] = useState(true);

  const save = useMutation({
    mutationFn: () => saveFn({
      data: {
        id: entry.id, page: entry.page, section: entry.section, item_key: entry.item_key,
        sort_order: order, is_published: published, data, image_url: imageUrl,
      },
    }),
    onSuccess: () => {
      setSavedAt(new Date().toLocaleTimeString());
      qc.invalidateQueries({ queryKey: ["site-content"] });
    },
  });

  const remove = useMutation({
    mutationFn: () => delFn({ data: { id: entry.id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-entries"] });
      qc.invalidateQueries({ queryKey: ["site-content"] });
    },
  });

  async function onFile(file: File) {
    setUploading(true);
    try {
      const dataUrl = await new Promise<string>((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result as string);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const out = await uploadFn({ data: { filename: file.name, dataUrl } });
      setImageUrl(out.url);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  const fields = Object.keys(data);

  const summary = (() => {
    for (const k of fields) {
      const v = data[k];
      if (isTField(v) && v.en) return v.en;
      if (typeof v === "string" && v) return v;
    }
    return "";
  })();

  return (
    <div className="rounded-xl border border-cream/10 bg-charcoal/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-cream/5 transition-colors"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className={`size-4 shrink-0 text-cream/50 transition-transform ${open ? "rotate-90" : ""}`}>
          <path d="M9 6l6 6-6 6" />
        </svg>
        <code className="text-xs text-yellow/80 shrink-0">{entry.item_key}</code>
        {summary && <span className="min-w-0 truncate text-sm text-cream/60">{summary}</span>}
        <span className="ml-auto shrink-0">
          {published
            ? <span className="rounded-full bg-emerald-400/15 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-400">Live</span>
            : <span className="rounded-full bg-cream/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-cream/50">Hidden</span>}
        </span>
      </button>

      {open && (
      <div className="border-t border-cream/10 p-4 sm:p-5">
      <div className="flex items-center justify-end gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-3 text-xs text-cream/70">
          <label className="flex items-center gap-1.5">
            <span>Order</span>
            <input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))}
              className="w-16 rounded-md border border-cream/15 bg-charcoal/60 px-2 py-1 text-cream" />
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
            <span>Published</span>
          </label>
        </div>
      </div>


      {("image" in entry || imageUrl !== null || entry.image_url !== null) && (
        <div className="mb-4 flex items-center gap-3">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="size-16 rounded-lg object-cover border border-cream/15" />
          ) : (
            <div className="size-16 rounded-lg border border-dashed border-cream/20 grid place-items-center text-cream/30 text-xs">No image</div>
          )}
          <label className="text-xs text-cream/70 cursor-pointer">
            <span className="rounded-full border border-cream/20 px-3 py-1.5 hover:border-yellow inline-block">
              {uploading ? "Uploading…" : "Upload image"}
            </span>
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
          </label>
          {imageUrl && <button onClick={() => setImageUrl(null)} className="text-xs text-chili hover:underline">Remove</button>}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((key) => {
          const value = data[key];
          const set = (v: unknown) => setData((d) => ({ ...d, [key]: v }));
          if (isTField(value)) {
            const long = (value.en?.length ?? 0) > 60 || (value.fi?.length ?? 0) > 60;
            return (
              <div key={key} className={long ? "sm:col-span-2" : ""}>
                <div className="text-[11px] uppercase tracking-widest text-cream/50 mb-1">{key.replace(/_/g, " ")}</div>
                <LangInput label={editLang.toUpperCase()} long={long}
                  value={value[editLang] ?? ""}
                  onChange={(v) => set({ ...value, [editLang]: v })} />
              </div>
            );
          }
          if (typeof value === "boolean") {
            return (
              <label key={key} className="flex items-center gap-2 text-sm text-cream/80 self-end pb-2">
                <input type="checkbox" checked={value} onChange={(e) => set(e.target.checked)} />
                <span>{key.replace(/_/g, " ")}</span>
              </label>
            );
          }
          if (typeof value === "number") {
            return (
              <label key={key} className="block">
                <span className="text-[11px] uppercase tracking-widest text-cream/50">{key.replace(/_/g, " ")}</span>
                <input type="number" value={value} onChange={(e) => set(Number(e.target.value))}
                  className="mt-1 w-32 rounded-lg border border-cream/15 bg-charcoal/60 px-3 py-2 text-cream" />
              </label>
            );
          }
          return (
            <label key={key} className="block">
              <span className="text-[11px] uppercase tracking-widest text-cream/50">{key.replace(/_/g, " ")}</span>
              <input type="text" value={String(value ?? "")} onChange={(e) => set(e.target.value)}
                className="mt-1 w-full rounded-lg border border-cream/15 bg-charcoal/60 px-3 py-2 text-cream text-sm" />
            </label>
          );
        })}
      </div>


      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          onClick={() => { if (confirm("Delete this item?")) remove.mutate(); }}
          className="text-xs text-chili hover:underline"
        >Delete</button>
        <div className="flex items-center gap-3">
          {savedAt && <span className="text-[11px] text-cream/40">Saved {savedAt}</span>}
          {save.error && <span className="text-[11px] text-chili">{(save.error as Error).message}</span>}
          <button
            onClick={() => save.mutate()} disabled={save.isPending}
            className="rounded-full bg-yellow text-charcoal px-5 py-2 text-xs uppercase tracking-widest font-bold hover:bg-cream disabled:opacity-60"
          >{save.isPending ? "Saving…" : "Save"}</button>
        </div>
      </div>
      </div>
      )}
    </div>
  );
}

function LangInput({ label, value, onChange, long }: { label: string; value: string; onChange: (v: string) => void; long: boolean }) {
  return (
    <div className="rounded-lg border border-cream/15 bg-charcoal/60 focus-within:border-yellow">
      <span className="block px-3 pt-1.5 text-[10px] uppercase tracking-widest text-cream/40">{label}</span>
      {long ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
          className="w-full bg-transparent px-3 pb-2 text-cream text-sm outline-none resize-y" />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent px-3 pb-2 text-cream text-sm outline-none" />
      )}
    </div>
  );
}

function MigrationManager() {
  const exportFn = useServerFn(exportDatabase);
  const importFn = useServerFn(importDatabase);
  const qc = useQueryClient();

  const [busy, setBusy] = useState<"export" | "import" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exportInfo, setExportInfo] = useState<string | null>(null);
  const [importInfo, setImportInfo] = useState<string | null>(null);
  const [pending, setPending] = useState<{ name: string; size: number; payload: unknown } | null>(null);

  async function handleExport() {
    setError(null); setExportInfo(null); setBusy("export");
    try {
      const snapshot = await exportFn();
      const json = JSON.stringify(snapshot);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `tacoya-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const c = snapshot.counts;
      setExportInfo(`Downloaded backup: ${c.content_entries} content rows, ${c.franchise_applications} applications, ${c.page_views} page views, ${c.images} images.`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    setError(null); setImportInfo(null); setPending(null);
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const payload = JSON.parse(String(reader.result));
        if (!payload || typeof payload !== "object" || !("tables" in payload)) {
          throw new Error("This file is not a valid Tacoya backup.");
        }
        setPending({ name: file.name, size: file.size, payload });
      } catch (err) {
        setError((err as Error).message);
      }
    };
    reader.onerror = () => setError("Could not read the selected file.");
    reader.readAsText(file);
  }

  async function confirmImport() {
    if (!pending) return;
    setError(null); setImportInfo(null); setBusy("import");
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await importFn({ data: pending.payload as any });
      const c = res.counts;
      setImportInfo(`Import complete: ${c.content_entries} content rows, ${c.franchise_applications} applications, ${c.page_views} page views, ${c.images} images restored.`);
      setPending(null);
      qc.invalidateQueries({ queryKey: ["admin-entries"] });
      qc.invalidateQueries({ queryKey: ["site-content"] });
      qc.invalidateQueries({ queryKey: ["applications"] });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="rounded-2xl border border-cream/10 bg-card p-6">
        <h2 className="font-display text-2xl text-cream uppercase tracking-wide">Backup &amp; Migration</h2>
        <p className="mt-2 text-sm text-cream/60">
          Export your entire site — all content, franchise applications, analytics, and uploaded images — into a single
          file. Use it as a backup, or import it into a remixed project to migrate everything with zero data loss.
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-chili/40 bg-chili/15 px-4 py-3 text-sm text-chili">{error}</div>
      )}

      {/* Export */}
      <div className="rounded-2xl border border-cream/10 bg-card p-6">
        <div className="flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl bg-yellow/15 text-yellow"><NavIcon name="download" /></span>
          <div>
            <h3 className="font-semibold text-cream">Export full database</h3>
            <p className="text-xs text-cream/55">Downloads a JSON snapshot including image data.</p>
          </div>
        </div>
        <button
          onClick={handleExport}
          disabled={busy !== null}
          className="mt-4 rounded-full bg-yellow text-charcoal px-5 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-cream disabled:opacity-60"
        >
          {busy === "export" ? "Exporting…" : "Export & download"}
        </button>
        {exportInfo && <p className="mt-3 text-sm text-cream/70">{exportInfo}</p>}
      </div>

      {/* Import */}
      <div className="rounded-2xl border border-cream/10 bg-card p-6">
        <div className="flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl bg-yellow/15 text-yellow"><NavIcon name="upload" /></span>
          <div>
            <h3 className="font-semibold text-cream">Import / restore</h3>
            <p className="text-xs text-cream/55">Re-uploads images and merges rows. Existing data is updated, not wiped.</p>
          </div>
        </div>

        <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full border border-cream/20 bg-charcoal px-5 py-2.5 text-xs uppercase tracking-widest font-bold text-cream hover:border-yellow hover:text-yellow">
          Choose backup file
          <input type="file" accept="application/json,.json" onChange={handleFile} className="hidden" disabled={busy !== null} />
        </label>

        {pending && (
          <div className="mt-4 rounded-xl border border-cream/10 bg-charcoal/60 p-4">
            <p className="text-sm text-cream/80">
              <span className="font-semibold text-cream">{pending.name}</span>{" "}
              <span className="text-cream/50">({(pending.size / 1024 / 1024).toFixed(2)} MB)</span>
            </p>
            <p className="mt-1 text-xs text-cream/55">Ready to import. This will add and update records in this project.</p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={confirmImport}
                disabled={busy !== null}
                className="rounded-full bg-yellow text-charcoal px-5 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-cream disabled:opacity-60"
              >
                {busy === "import" ? "Importing…" : "Confirm import"}
              </button>
              <button
                onClick={() => setPending(null)}
                disabled={busy !== null}
                className="rounded-full border border-cream/20 px-5 py-2.5 text-xs uppercase tracking-widest font-bold text-cream/70 hover:text-cream disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {importInfo && <p className="mt-3 text-sm text-lime">{importInfo}</p>}
      </div>

      <p className="text-xs text-cream/40">
        Note: admin accounts are tied to login credentials and are not included in the backup. After importing into a new
        project, sign up and claim admin access there.
      </p>
    </div>
  );
}


function Centered({ children }: { children: React.ReactNode }) {
  return <div className="min-h-dvh grid place-items-center bg-charcoal px-4 text-cream/80">{children}</div>;
}
